// composables/useToast.js
import { ref, reactive, nextTick } from 'vue';

// Global toast state
const toasts = ref([]);
let toastIdCounter = 0;

// Default configuration
const defaultConfig = {
  duration: 5000,
  position: 'top-right',
  maxToasts: 5,
  pauseOnHover: true,
  showProgress: false,
  closeOnClick: false,
  dismissible: true,
  animation: 'slide',
};

// Toast store with reactive configuration
const toastStore = reactive({
  config: { ...defaultConfig },
  toasts: toasts,
  pausedToasts: new Set(),
  timers: new Map(),
});

/**
 * Enhanced Toast Composable for Vue 3
 * Provides comprehensive toast notification system with advanced features
 */
export const useToast = () => {
  /**
   * Add a new toast notification
   * @param {Object} options - Toast configuration
   * @returns {number} Toast ID
   */
  const addToast = options => {
    // Normalize options
    const toastOptions = normalizeToastOptions(options);

    // Create toast object
    const toast = {
      id: ++toastIdCounter,
      createdAt: Date.now(),
      ...toastOptions,
    };

    // Respect max toasts limit
    if (toasts.value.length >= toastStore.config.maxToasts) {
      // Remove oldest non-persistent toast
      const oldestRemovable = toasts.value.find(t => !t.persistent);
      if (oldestRemovable) {
        removeToast(oldestRemovable.id);
      }
    }

    // Add toast to array
    toasts.value.push(toast);

    // Setup auto removal timer if not persistent
    if (toast.duration > 0 && !toast.persistent) {
      setupAutoRemoval(toast);
    }

    // Trigger sound if enabled
    if (toast.sound) {
      playNotificationSound(toast.type);
    }

    return toast.id;
  };

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID
   */
  const removeToast = id => {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index === -1) return;

    // Clear any existing timer
    clearToastTimer(id);

    // Remove from paused set
    toastStore.pausedToasts.delete(id);

    // Remove toast
    toasts.value.splice(index, 1);
  };

  /**
   * Remove all toasts
   * @param {string} type - Optional: only remove toasts of specific type
   */
  const removeAllToasts = (type = null) => {
    if (type) {
      const toastsToRemove = toasts.value
        .filter(t => t.type === type && t.dismissible !== false)
        .map(t => t.id);

      toastsToRemove.forEach(id => removeToast(id));
    } else {
      // Clear all timers
      toastStore.timers.forEach((timer, id) => {
        clearTimeout(timer);
      });
      toastStore.timers.clear();
      toastStore.pausedToasts.clear();

      // Remove all dismissible toasts
      toasts.value = toasts.value.filter(t => t.dismissible === false);
    }
  };

  /**
   * Update existing toast
   * @param {number} id - Toast ID
   * @param {Object} updates - Properties to update
   */
  const updateToast = (id, updates) => {
    const toast = toasts.value.find(t => t.id === id);
    if (!toast) return false;

    // Apply updates
    Object.assign(toast, updates);

    // Reset timer if duration changed
    if (updates.duration !== undefined && updates.duration > 0) {
      clearToastTimer(id);
      setupAutoRemoval(toast);
    }

    return true;
  };

  /**
   * Pause auto-removal for a toast
   * @param {number} id - Toast ID
   */
  const pauseToast = id => {
    if (toastStore.timers.has(id)) {
      clearTimeout(toastStore.timers.get(id));
      toastStore.timers.delete(id);
      toastStore.pausedToasts.add(id);
    }
  };

  /**
   * Resume auto-removal for a toast
   * @param {number} id - Toast ID
   */
  const resumeToast = id => {
    const toast = toasts.value.find(t => t.id === id);
    if (toast && toastStore.pausedToasts.has(id)) {
      toastStore.pausedToasts.delete(id);

      // Calculate remaining time
      const elapsed = Date.now() - toast.createdAt;
      const remaining = Math.max(0, toast.duration - elapsed);

      if (remaining > 0) {
        setupAutoRemoval(toast, remaining);
      } else {
        removeToast(id);
      }
    }
  };

  /**
   * Check if toast is paused
   * @param {number} id - Toast ID
   * @returns {boolean}
   */
  const isToastPaused = id => {
    return toastStore.pausedToasts.has(id);
  };

  /**
   * Update global configuration
   * @param {Object} config - Configuration updates
   */
  const updateConfig = config => {
    Object.assign(toastStore.config, config);
  };

  // Convenience methods for different toast types
  const success = (message, options = {}) => {
    return addToast({
      message,
      type: 'success',
      ...options,
    });
  };

  const error = (message, options = {}) => {
    return addToast({
      message,
      type: 'error',
      duration: options.duration || 8000, // Longer for errors
      ...options,
    });
  };

  const warning = (message, options = {}) => {
    return addToast({
      message,
      type: 'warning',
      duration: options.duration || 6000,
      ...options,
    });
  };

  const info = (message, options = {}) => {
    return addToast({
      message,
      type: 'info',
      ...options,
    });
  };

  const loading = (message, options = {}) => {
    return addToast({
      message,
      type: 'loading',
      persistent: true,
      dismissible: false,
      showProgress: true,
      ...options,
    });
  };

  const promise = async (promiseInstance, messages = {}) => {
    const defaultMessages = {
      loading: 'Loading...',
      success: 'Success!',
      error: 'Something went wrong!',
    };

    const msgs = { ...defaultMessages, ...messages };

    // Show loading toast
    const loadingToastId = loading(msgs.loading);

    try {
      const result = await promiseInstance;

      // Remove loading toast and show success
      removeToast(loadingToastId);
      success(
        typeof msgs.success === 'function' ? msgs.success(result) : msgs.success
      );

      return result;
    } catch (error) {
      // Remove loading toast and show error
      removeToast(loadingToastId);
      const errorMessage =
        typeof msgs.error === 'function' ? msgs.error(error) : msgs.error;
      error(errorMessage);

      throw error;
    }
  };

  // Helper functions
  const normalizeToastOptions = options => {
    if (typeof options === 'string') {
      options = { message: options };
    }

    return {
      type: 'info',
      duration: toastStore.config.duration,
      dismissible: toastStore.config.dismissible,
      showProgress: toastStore.config.showProgress,
      closeOnClick: toastStore.config.closeOnClick,
      pauseOnHover: toastStore.config.pauseOnHover,
      persistent: false,
      html: false,
      sound: false,
      ...options,
    };
  };

  const setupAutoRemoval = (toast, duration = toast.duration) => {
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, duration);

    toastStore.timers.set(toast.id, timer);
  };

  const clearToastTimer = id => {
    if (toastStore.timers.has(id)) {
      clearTimeout(toastStore.timers.get(id));
      toastStore.timers.delete(id);
    }
  };

  const playNotificationSound = type => {
    // Only play sound in browser environment
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;

    try {
      // You can customize sound files based on type
      const soundMap = {
        success: '/sounds/success.mp3',
        error: '/sounds/error.mp3',
        warning: '/sounds/warning.mp3',
        info: '/sounds/info.mp3',
      };

      const soundFile = soundMap[type] || soundMap.info;
      const audio = new Audio(soundFile);
      audio.volume = 0.3; // Keep it subtle
      audio.play().catch(() => {
        // Ignore audio play errors (user interaction required, etc.)
      });
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  };

  return {
    // State
    toasts,
    config: toastStore.config,

    // Core methods
    addToast,
    removeToast,
    removeAllToasts,
    updateToast,

    // Control methods
    pauseToast,
    resumeToast,
    isToastPaused,
    updateConfig,

    // Convenience methods
    success,
    error,
    warning,
    info,
    loading,
    promise,
  };
};

// Create global toast instance for use across the app
const globalToast = useToast();

// Export convenience methods for global use
export const toast = {
  success: globalToast.success,
  error: globalToast.error,
  warning: globalToast.warning,
  info: globalToast.info,
  loading: globalToast.loading,
  promise: globalToast.promise,
  remove: globalToast.removeToast,
  removeAll: globalToast.removeAllToasts,
  update: globalToast.updateToast,
};

// Default export for backward compatibility
export default globalToast;
